import { addDays, endOfDay, isAfter, isBefore } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  rootId,
  TreeItem,
  TreeItemTypes,
} from 'shared/components/ui/virtualized-tree-list';
import { ID } from 'shared/types/id';
import {
  noDeadlineSectionID,
  overdueSectionID,
} from 'shared/types/orderable-section';
import { SortingMode } from 'shared/types/sorting-mode';
import { Task } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { formatDateKey } from 'shared/utils/format-date-key';
import { getSortedTasks } from 'shared/utils/get-sorted-tasks';
import { getDueTaskPlanning } from 'shared/utils/get-task-planning';

export type Options = {
  tasks: Task[];
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
  showEmptyOverdue?: boolean;
  showEmptyNoDate?: boolean;
  showEmptyDate?: boolean;
  dateFilterEndDate?: Timestamp | null;
};

export const useDeadlineTree = ({
  tasks,
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
  showEmptyOverdue,
  showEmptyNoDate,
  showEmptyDate,
  dateFilterEndDate,
}: Options): Record<ID, TreeItem> => {
  const { t } = useTranslation();

  return useMemo(() => {
    const overdueDeadline = endOfDay(addDays(today, -1));
    const dateSections: Record<string, { date: Timestamp; taskIds: ID[] }> = {};
    const overdueTaskIds: ID[] = [];
    const overdueTasks: Task[] = [];
    const standardTasksMap = new Map<ID, Task>();
    const noDeadlineTaskIds: ID[] = [];

    // Process each task to determine its deadline sections
    tasks.forEach((task) => {
      if (task.completedAt) {
        return;
      }

      // Determine effective deadline based on "Earliest Date" rule:
      const effectiveDeadline =
        !task.schedules?.length &&
        (task.endStrategy?.deadline || task.deadlineDeadline)
          ? new Date(
              Math.min(
                ...([
                  task.endStrategy?.deadline?.getTime(),
                  task.deadlineDeadline?.getTime(),
                ].filter(
                  (timestamp) => typeof timestamp === 'number',
                ) as number[]),
              ),
            )
          : null;

      // Check if task is overdue using same logic as use-due-status-grouped-tasks
      const overduePlanning = getDueTaskPlanning(task, {
        date: overdueDeadline,
        today,
        weekStartsOn,
      });

      const duePlanning = overduePlanning.filter(
        (plannedOccurrence) =>
          !isAfter(plannedOccurrence.date, overdueDeadline),
      );

      const plannedOccurrence = duePlanning[duePlanning.length - 1];
      const isOverdue =
        !!plannedOccurrence ||
        (!!effectiveDeadline && !isAfter(effectiveDeadline, overdueDeadline));

      // Get all planned occurrences for grouping (using today as reference)
      const planning = effectiveDeadline
        ? []
        : getDueTaskPlanning(task, {
            date: today,
            today,
            weekStartsOn,
          });

      // Task has no deadline if:
      // 1. No planned occurrences (schedules)
      // 2. No effective deadline
      if (!planning.length && !effectiveDeadline) {
        noDeadlineTaskIds.push(task.id);
        standardTasksMap.set(task.id, task);
        return;
      }

      // If task is overdue, add to overdue section
      if (isOverdue) {
        overdueTaskIds.push(task.id);
        overdueTasks.push(task);
      }

      // Collect all dates this task should appear in (for future/current dates)
      const taskDates = new Set<string>();

      // Add dates from planned occurrences (for scheduled tasks)
      // Only add dates that are today or in the future (not overdue)
      planning.forEach(({ date }) => {
        if (!isBefore(date, today)) {
          // If we have a date filter end date, and this is a habit or repeating task,
          // do not add dates beyond the filter end date.
          if (
            dateFilterEndDate &&
            isAfter(date, dateFilterEndDate) &&
            task.type !== TaskType.Task
          ) {
            return;
          }

          const dateKey = formatDateKey(date);
          taskDates.add(dateKey);
        }
      });

      // Add effectiveDeadline if it exists and is today or future
      if (effectiveDeadline && !isBefore(effectiveDeadline, today)) {
        // If we have a date filter end date, and this is a habit or repeating task,
        // do not add dates beyond the filter end date.
        const shouldSkip =
          dateFilterEndDate &&
          isAfter(effectiveDeadline, dateFilterEndDate) &&
          task.type !== TaskType.Task;

        if (!shouldSkip) {
          const dateKey = formatDateKey(effectiveDeadline);
          taskDates.add(dateKey);
        }
      }

      // Add task to appropriate date sections (for future/current dates)
      taskDates.forEach((dateKey) => {
        if (!dateSections[dateKey]) {
          dateSections[dateKey] = {
            date: new Date(dateKey),
            taskIds: [],
          };
        }
        dateSections[dateKey].taskIds.push(task.id);
        standardTasksMap.set(task.id, task);
      });
    });

    if (showEmptyDate) {
      const todayKey = formatDateKey(today);
      if (!dateSections[todayKey]) {
        dateSections[todayKey] = {
          date: today,
          taskIds: [],
        };
      }
    }

    // Build tree items for date sections
    const dateTreeItems: Record<ID, TreeItem> = {};

    // Add overdue section if there are overdue tasks
    if (overdueTaskIds.length || showEmptyOverdue) {
      dateTreeItems[overdueSectionID] = {
        id: overdueSectionID,
        type: TreeItemTypes.Date,
        name: t('general.sections.overdue.title'),
        children: [],
        isFolder: true,
      };
    }

    // Sort date sections chronologically and create tree items
    const sortedDateKeys = Object.keys(dateSections).sort(
      (a, b) => dateSections[a].date.getTime() - dateSections[b].date.getTime(),
    );

    sortedDateKeys.forEach((dateKey) => {
      dateTreeItems[dateKey] = {
        id: dateKey,
        type: TreeItemTypes.Date,
        name: dateKey, // Will be formatted in header component
        children: [],
        isFolder: true,
      };
    });

    // Add "No deadline" section if there are tasks without deadlines
    if (noDeadlineTaskIds.length || showEmptyNoDate) {
      dateTreeItems[noDeadlineSectionID] = {
        id: noDeadlineSectionID,
        type: TreeItemTypes.Date,
        name: t('general.labels.no-date'),
        children: [],
        isFolder: true,
      };
    }

    // Get custom order for all sections
    const customTaskOrder =
      sortingMode === SortingMode.Custom
        ? Object.keys(dateTreeItems).reduce<ID[]>((acc, sectionId) => {
            const sectionOrder = customOrder?.[sectionId] ?? [];
            acc.push(...sectionOrder);
            return acc;
          }, [])
        : [];

    // Sort tasks and assign to sections
    // Sort tasks for Overdue section
    const overdueSortedTasks = getSortedTasks(overdueTasks, {
      today,
      weekStartsOn,
      order: customTaskOrder,
      mode: [SortingMode.DueDate],
      allDayFirst: true,
      invertedTimeslots: true,
    });

    // Populate Overdue Section
    overdueSortedTasks.forEach((task) => {
      if (dateTreeItems[overdueSectionID]) {
        dateTreeItems[overdueSectionID].children?.push(task.id);
      }

      dateTreeItems[task.id] = {
        id: task.id,
        name: task.name,
        type: TreeItemTypes.Task,
      };
    });

    // Sort tasks for other sections
    const standardSortedTasks = getSortedTasks(
      Array.from(standardTasksMap.values()),
      {
        today,
        weekStartsOn,
        order: customTaskOrder,
        mode: sortingMode ? [SortingMode.DueDate, sortingMode] : undefined,
        allDayFirst: true,
      },
    );

    // Populate other sections
    standardSortedTasks.forEach((task) => {
      // Handle No Deadline
      if (noDeadlineTaskIds.includes(task.id)) {
        if (dateTreeItems[noDeadlineSectionID]) {
          dateTreeItems[noDeadlineSectionID].children?.push(task.id);
        }

        // Add task tree item
        dateTreeItems[task.id] = {
          id: task.id,
          name: task.name,
          type: TreeItemTypes.Task,
        };

        return;
      }

      // Handle Date Sections
      Object.keys(dateSections).forEach((dateKey) => {
        if (dateSections[dateKey].taskIds.includes(task.id)) {
          if (dateTreeItems[dateKey]) {
            dateTreeItems[dateKey].children?.push(task.id);
          }
        }
      });

      // Add task tree item
      dateTreeItems[task.id] = {
        id: task.id,
        name: task.name,
        type: TreeItemTypes.Task,
      };
    });

    const allTreeItems = dateTreeItems;

    // Add New Task row to each section

    // Date Sections
    Object.keys(dateTreeItems).forEach((dateKey) => {
      // Skip non-date sections we already handled or will handle
      if (
        dateKey === overdueSectionID ||
        dateKey === noDeadlineSectionID ||
        !dateTreeItems[dateKey].children
      ) {
        return;
      }
      const section = dateTreeItems[dateKey];
      if (section) {
        const newTaskItemId = `${dateKey}-new-task`;
        section.children?.push(newTaskItemId);
        allTreeItems[newTaskItemId] = {
          id: newTaskItemId,
          name: 'New Task',
          type: TreeItemTypes.NewTask,
        };
      }
    });

    // No Deadline
    if (dateTreeItems[noDeadlineSectionID]) {
      const newTaskItemId = `${noDeadlineSectionID}-new-task`;
      dateTreeItems[noDeadlineSectionID].children?.push(newTaskItemId);
      allTreeItems[newTaskItemId] = {
        id: newTaskItemId,
        name: 'New Task',
        type: TreeItemTypes.NewTask,
      };
    }

    // Build section IDs in order: overdue, date sections (chronological), no deadline
    const sectionIds: ID[] = [];
    if (dateTreeItems[overdueSectionID]) {
      sectionIds.push(overdueSectionID);
    }
    sectionIds.push(...sortedDateKeys);
    if (dateTreeItems[noDeadlineSectionID]) {
      sectionIds.push(noDeadlineSectionID);
    }

    return {
      [rootId]: {
        id: rootId,
        name: 'Root',
        type: TreeItemTypes.Root,
        children: sectionIds,
      },
      ...allTreeItems,
    };
  }, [
    today,
    tasks,
    showEmptyDate,
    showEmptyOverdue,
    showEmptyNoDate,
    sortingMode,
    weekStartsOn,
    dateFilterEndDate,
    t,
    customOrder,
  ]);
};
